import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  EyeColumnV8,
  toastFailure,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  isActiveColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
} from 'shared-pages/common/grid-helpfunc';
import { FieldCollectionPointDlg } from 'shared-pages/components';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { isRebattery } from 'shared-pages/common/constants';
import ButtonNewRecord from 'shared-pages/components/buttons/button-new-record';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
    collectionpointId,
    canAddNew,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateRecord] = useMutation(gqlUpdate);
  const [addNewRecord] = useMutation(gqlAdd);
  const [deleteRecord] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      if (isGridInForm) {
        history.push({
          pathname: generatePath(getRouteByName('CollectionpointContact'), { id: row.id }),
          state: {
            fromCollectionpoint: true,
            collectionpointId: row.collectionpointId,
          },
        });
      } else {
        history.push({
          pathname: generatePath(getRouteByName('CollectionpointContact'), { id: row.id }),
        });
      }
    },
    [history, isGridInForm],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'name',
        header: t('Ονομα'),
        columnName: t('Ονομα'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      ...(!isGridInForm
        ? [
            {
              id: 'collectionpointId',
              header: t('Σημείο Συλλογής'),
              columnName: t('Σημείο Συλλογής'),
              accessorKey: 'collectionpoint.name',
              orderByField: 'collectionpoint.name',
              cell: (props) => {
                const { getValue, row, column, table } = props;
                const tableMeta = table.options.meta;
                const id = props.row.id;
                const isEditing = tableMeta?.editedRows[id];
                const value = props.getValue('collectionpoint.name');

                if (isEditing) {
                  return (
                    <>
                      <FieldCollectionPointDlg
                        initValueId={id}
                        initValueName={value}
                        onValueChange={(valueId, valueName) => {
                          tableMeta.updateData(row.index, column.id, valueId, row);
                        }}
                        initFilters={{ isActive: true }}
                      />
                    </>
                  );
                } else return <div>{value}</div>;
              },
              size: 20,
              meta: { type: 'text' },
            },
          ]
        : []),
      ...(!isRebattery
        ? [
            {
              id: 'phone',
              header: t('Τηλέφωνο'),
              columnName: t('Τηλέφωνο'),
              accessorKey: 'phone',
              orderByField: 'phone',
              cell: TableCellV8,
              size: 20,
              meta: { type: 'text' },
            },
          ]
        : []),

      isActiveColumn(t, i18n),
      // firstDateUserColumn(t),
      // lastDateUserColumn(t),
      ...(appStore.isPlatformOwner
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 120,
              cell: ({ row, table }) => {
                return true ? EditCellV8({ row, table }) : <>-</>;
              },
            },
          ]
        : []),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          style={{ overflow: 'visible' }}
          ref={tableRef}
          loading={loading}
          id="contacts-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
              collectorId: appStore.isCollector ? appStore.associatedId : undefined,
              collectionpointId: collectionpointId,
            };
          }}
          onBeforeSaveRow={async (state, row, changes) => {
            console.log('$$$ onBeforeSaveRow', state, row, changes);
            if (row?.isNotActive !== undefined && row.isNotActive === true) return true;
            // Validate
            if (!row.collectionpointId && !changes.collectionpointId) {
              toastFailure(t('Πρέπει να δηλώσετε Σημείο Συλλογής'));
              return false;
            }
            return true;
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Contacts - OnSaveRow row', row, changes);
            const values =
              tableStore.state === TableDataState.insert ? { ...row } : { id: id, ...changes }; // Το changes δεν έχει το collectorId
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewRecord({ variables: { data: mutValues } }).catch((reason) => {
                console.error('$$$ grid.UpdateRow insert error reason', reason);
              });
              if (!res) return false; // some error returned
              id = res.data.addCollectionpointsContact.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateRecord({ variables: { data: mutValues } }).catch((reason) => {
                console.error('$$$ grid.UpdateRow insert error reason', reason);
              });
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε την Επαφή <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteRecord({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Οδηγός');
            return true;
          }}
          injectFooterElement={
            <>
              {canAddNew && (
                <ButtonNewRecord
                  onClick={() => {
                    const tblMeta = tableRef.current
                      ? (tableRef.current as any).table.options.meta
                      : undefined;
                    if (tblMeta) {
                      if (tblMeta.dataState === TableDataState.browse) {
                        tblMeta.addNewRow();
                      }
                    }
                  }}
                  label={t('Νέα Επαφή')}
                />
              )}
            </>
          }
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
